"use client";

import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  /** Delay in ms before animation starts (useful for stagger) */
  delay?: number;
  id?: string;
  /** `fade-up` (default) adds a subtle rise; `fade` is opacity-only. */
  variant?: "fade-up" | "fade";
}

export function ScrollReveal({
  children,
  className,
  delay = 0,
  id,
  variant = "fade-up",
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect reduced motion preference
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) {
      setIsVisible(true);
      return;
    }

    const reveal = () => {
      if (delay > 0) {
        return setTimeout(() => setIsVisible(true), delay);
      }
      setIsVisible(true);
      return undefined;
    };

    // Show immediately when already in (or near) the viewport on mount.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight + 80 && rect.bottom > -80) {
      const timeoutId = reveal();
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
      };
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          timeoutId = reveal();
          observer.unobserve(el);
        }
      },
      { threshold: 0.1, rootMargin: "-40px 0px" }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [delay]);

  return (
    <div
      ref={ref}
      id={id}
      className={cn(
        "duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none",
        variant === "fade"
          ? "transition-opacity"
          : "transition-[opacity,transform]",
        isVisible ? "opacity-100" : "opacity-0",
        variant === "fade-up" &&
          (isVisible
            ? "motion-safe:translate-y-0"
            : "motion-safe:translate-y-4"),
        className
      )}
    >
      {children}
    </div>
  );
}
